import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, InputNumber, message, Modal, Select, Space, Switch, Table, Tag, Tooltip, Typography, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UploadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { employeeUserService } from '../services/employeeUserService';
import { workScheduleService } from '../services/workScheduleService';
import { ExternalPayrollItem, PayrollDayDetail, payrollTimesheetService, PayrollTimesheetImport } from '../services/payrollTimesheetService';
import { DefaultPosition, EmployeeUser, EmploymentType } from '../types/employeeUser';
import * as XLSX from 'xlsx';
import '../styles/AdminPages.css';
import type { RcFile } from 'antd/es/upload';

const { Search } = Input;
const { Text } = Typography;

const HEADER_KEY = 'Tên nhân viên';
const PREFERRED_SHEET = 'Xuất lưới';

interface PayrollSummaryRow {
  rowId: string;
  wageKey: string;
  userId: string;
  name: string;
  hourlyWage: number;
  totalHours: number;
  totalPay: number;
  isExternal?: boolean;
}

interface PayrollDetailModalState {
  open: boolean;
  row: PayrollSummaryRow | null;
}

interface WageEditorModalState {
  open: boolean;
  row: PayrollSummaryRow | null;
  value: number;
}

interface ImportedTimesheetRow {
  ngay: string;
  maNV: string;
  tenNhanVien: string;
  totalHours: number;
}

interface MonthlyImportCacheEntry {
  monthlyData: PayrollTimesheetImport;
  monthRows: PayrollDayDetail[];
}

const buildTimesheetRowKey = (row: ImportedTimesheetRow): string => {
  const dayKey = row.ngay.trim();
  const employeeKey = row.maNV.trim() || normalizeText(row.tenNhanVien.trim());
  return `${dayKey}-${employeeKey}`;
};

const roundTo2 = (value: number): number => Math.round(value * 100) / 100;

const makeExternalWageKey = (maNV: string, name: string): string => {
  return `ext:${maNV || 'N/A'}:${normalizeText(name || 'unknown')}`;
};

const formatDateValue = (value: unknown): string => {
  if (!value) {
    return '';
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format('DD/MM/YYYY');
  }

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) {
      return '';
    }
    const dd = String(parsed.d).padStart(2, '0');
    const mm = String(parsed.m).padStart(2, '0');
    return `${dd}/${mm}/${parsed.y}`;
  }

  return String(value).trim();
};

const formatTextValue = (value: unknown): string => {
  if (value == null) {
    return '';
  }

  return String(value).trim();
};

const formatNumberValue = (value: unknown): number => {
  if (value == null || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const textValue = String(value).trim();
  if (/^\d{1,2}:\d{1,2}(:\d{1,2})?$/.test(textValue)) {
    const [hourPart, minutePart = '0'] = textValue.split(':');
    const hour = Number(hourPart);
    const minute = Number(minutePart);
    if (Number.isFinite(hour) && Number.isFinite(minute)) {
      return roundTo2(hour + minute / 60);
    }
  }

  const normalized = textValue.replace(',', '.').replace(/[^0-9.-]/g, '');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : roundTo2(parsed);
};

const normalizeText = (value: string): string => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .toLowerCase();
};

const findTargetWorksheet = (workbook: XLSX.WorkBook): XLSX.WorkSheet => {
  const sheetName = workbook.SheetNames.find((name) => name === PREFERRED_SHEET) ?? workbook.SheetNames[0];

  if (!sheetName) {
    throw new Error('File Excel không có sheet nào.');
  }

  return workbook.Sheets[sheetName];
};

const extractRowsFromWorksheet = (worksheet: XLSX.WorkSheet): ImportedTimesheetRow[] => {
  const rawRows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    header: 1,
    raw: true,
    defval: '',
  });

  const headerIndex = rawRows.findIndex((row) => row.some((cell) => formatTextValue(cell) === HEADER_KEY));
  if (headerIndex === -1) {
    throw new Error('Không tìm thấy dòng tiêu đề chứa "Tên nhân viên" trong file Excel.');
  }

  return rawRows
    .slice(headerIndex + 1)
    .filter((row) => row.some((cell) => formatTextValue(cell) !== ''))
    .map((row) => ({
      ngay: formatDateValue(row[0]),
      maNV: formatTextValue(row[2]),
      tenNhanVien: formatTextValue(row[3]),
      totalHours: formatNumberValue(row[10]),
    }))
    .filter((row) => row.totalHours > 0 && (Boolean(row.maNV) || Boolean(row.tenNhanVien)));
};

const formatCurrency = (amount: number): string => {
  return `${amount.toLocaleString('vi-VN')} đ`;
};

const getRoleTag = (user: EmployeeUser): React.ReactNode => {
  if (user.isAdmin) {
    return <Tag color="red">Admin</Tag>;
  }
  if (user.dataEntryRole) {
    return <Tag color="blue">Nhập liệu</Tag>;
  }
  return <Tag>Nhân viên</Tag>;
};

const getEmploymentText = (employmentType?: EmploymentType): string => {
  if (employmentType === 'fulltime') {
    return 'Toàn thời gian';
  }
  if (employmentType === 'parttime') {
    return 'Bán thời gian';
  }
  return 'Chưa thiết lập';
};

const getPositionText = (position?: DefaultPosition): string => {
  if (position === 'table') {
    return 'Table';
  }
  if (position === 'bar') {
    return 'Bar';
  }
  if (position === 'security') {
    return 'Security';
  }
  return 'Chưa thiết lập';
};

const EmployeeRoleManagement: React.FC = () => {
  const [employees, setEmployees] = useState<EmployeeUser[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  const [employeeModalOpen, setEmployeeModalOpen] = useState<boolean>(false);
  const [editingEmployee, setEditingEmployee] = useState<EmployeeUser | null>(null);
  const [savingEmployee, setSavingEmployee] = useState<boolean>(false);
  const [employeeForm] = Form.useForm();

  const [salaryPeriodValue, setSalaryPeriodValue] = useState<Dayjs>(dayjs());
  const [payrollRows, setPayrollRows] = useState<PayrollSummaryRow[]>([]);
  const [loadingPayroll, setLoadingPayroll] = useState<boolean>(false);
  const [savingWageOverrides, setSavingWageOverrides] = useState<boolean>(false);
  const [wageOverrides, setWageOverrides] = useState<Record<string, number>>({});
  const [rowDetailsByWageKey, setRowDetailsByWageKey] = useState<Record<string, PayrollDayDetail[]>>({});
  const [monthlyTimesheetRows, setMonthlyTimesheetRows] = useState<PayrollDayDetail[]>([]);
  const [monthlyImports, setMonthlyImports] = useState<PayrollTimesheetImport[]>([]);
  const [loadingMonthlyImports, setLoadingMonthlyImports] = useState<boolean>(false);
  const [selectedImportMonthKey, setSelectedImportMonthKey] = useState<string>('');
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [importMonthValue, setImportMonthValue] = useState<Dayjs>(dayjs());
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [parsingExcel, setParsingExcel] = useState<boolean>(false);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [importedHoursMap, setImportedHoursMap] = useState<Record<string, number>>({});
  const [externalPayrollItems, setExternalPayrollItems] = useState<ExternalPayrollItem[]>([]);
  const [unmatchedRows, setUnmatchedRows] = useState<string[]>([]);
  const [payrollDetailModal, setPayrollDetailModal] = useState<PayrollDetailModalState>({ open: false, row: null });
  const [wageEditorModal, setWageEditorModal] = useState<WageEditorModalState>({ open: false, row: null, value: 0 });
  const isMountedRef = useRef<boolean>(true);
  const selectedImportMonthKeyRef = useRef<string>('');
  const employeesRequestRef = useRef<Promise<EmployeeUser[]> | null>(null);
  const monthlyImportsRequestRef = useRef<Promise<PayrollTimesheetImport[]> | null>(null);
  const monthlyImportCacheRef = useRef<Map<string, MonthlyImportCacheEntry>>(new Map());

  const isMobile = windowWidth <= 768;

  const periodLabel = useMemo(() => {
    return salaryPeriodValue.format('MM/YYYY');
  }, [salaryPeriodValue]);

  const filteredEmployees = useMemo(() => {
    const normalized = searchText.trim().toLowerCase();
    if (!normalized) {
      return employees;
    }

    return employees.filter((item) => {
      return (
        item.name.toLowerCase().includes(normalized) ||
        item.userId.toLowerCase().includes(normalized) ||
        item.idByOA.toLowerCase().includes(normalized)
      );
    });
  }, [employees, searchText]);

  const applyMonthlyImportToState = useCallback((monthlyData: PayrollTimesheetImport, monthRows: PayrollDayDetail[]) => {
    setSelectedImportMonthKey(monthlyData.monthKey);
    setSalaryPeriodValue(dayjs(`${monthlyData.monthKey}-01`));
    setUploadedFileName(monthlyData.fileName);
    setImportedHoursMap(monthlyData.hoursByUserId ?? {});
    setExternalPayrollItems(monthlyData.externalItems ?? []);
    setUnmatchedRows(monthlyData.unmatchedRows ?? []);
    setWageOverrides(monthlyData.wageOverrides ?? {});
    setRowDetailsByWageKey(monthlyData.rowDetailsByWageKey ?? {});
    setMonthlyTimesheetRows(monthRows);
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    selectedImportMonthKeyRef.current = selectedImportMonthKey;
  }, [selectedImportMonthKey]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadEmployees = useCallback(async () => {
    if (employeesRequestRef.current) {
      await employeesRequestRef.current;
      return;
    }

    setLoadingEmployees(true);
    const request = employeeUserService.getAllUsersDeduplicated();
    employeesRequestRef.current = request;
    try {
      const data = await request;
      if (!isMountedRef.current) {
        return;
      }
      setEmployees(data);
    } catch (error) {
      if (isMountedRef.current) {
        message.error('Không thể tải danh sách nhân viên');
      }
    } finally {
      employeesRequestRef.current = null;
      if (isMountedRef.current) {
        setLoadingEmployees(false);
      }
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const loadMonthlyImports = useCallback(async (autoSelectLatest = true) => {
    if (monthlyImportsRequestRef.current) {
      await monthlyImportsRequestRef.current;
      return;
    }

    setLoadingMonthlyImports(true);
    const request = payrollTimesheetService.getAllMonthlyImports();
    monthlyImportsRequestRef.current = request;
    try {
      const data = await request;
      if (!isMountedRef.current) {
        return;
      }

      setMonthlyImports(data);

      if (autoSelectLatest && data.length > 0 && !selectedImportMonthKeyRef.current) {
        const latest = data[0];
        const cached = monthlyImportCacheRef.current.get(latest.monthKey);
        const monthRows = cached?.monthRows ?? await payrollTimesheetService.getMonthlyRows(latest.monthKey);
        monthlyImportCacheRef.current.set(latest.monthKey, {
          monthlyData: latest,
          monthRows,
        });
        if (isMountedRef.current) {
          applyMonthlyImportToState(latest, monthRows);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        message.error('Không thể tải danh sách tháng đã upload');
        setMonthlyImports([]);
      }
    } finally {
      monthlyImportsRequestRef.current = null;
      if (isMountedRef.current) {
        setLoadingMonthlyImports(false);
      }
    }
  }, [applyMonthlyImportToState]);

  useEffect(() => {
    loadMonthlyImports();
  }, [loadMonthlyImports]);

  const parseTimesheetRows = (rows: Record<string, unknown>[]): ImportedTimesheetRow[] => {
    if (rows.length === 0) {
      return [];
    }

    return rows
      .map((row) => ({
        ngay: formatTextValue(row.ngay),
        maNV: formatTextValue(row.maNV),
        tenNhanVien: formatTextValue(row.tenNhanVien),
        totalHours: formatNumberValue(row.totalHours),
      }))
      .filter((row) => row.totalHours > 0 && (Boolean(row.maNV) || Boolean(row.tenNhanVien)));
  };

  const deduplicateTimesheetRows = (rows: ImportedTimesheetRow[]): { rows: ImportedTimesheetRow[]; duplicateCount: number } => {
    const dedupedMap = new Map<string, ImportedTimesheetRow>();
    let duplicateCount = 0;

    rows.forEach((row) => {
      const key = buildTimesheetRowKey(row);
      const existing = dedupedMap.get(key);
      if (!existing) {
        dedupedMap.set(key, row);
        return;
      }

      duplicateCount += 1;
      if (row.totalHours > existing.totalHours) {
        dedupedMap.set(key, row);
      }
    });

    return {
      rows: Array.from(dedupedMap.values()),
      duplicateCount,
    };
  };

  const mapImportedRowsToEmployees = (
    rows: ImportedTimesheetRow[]
  ): {
    hoursMap: Record<string, number>;
    notMatched: string[];
    externalItems: ExternalPayrollItem[];
    detailsByWageKey: Record<string, PayrollDayDetail[]>;
  } => {
    const employeeByUserId = new Map<string, EmployeeUser>();
    const employeeByName = new Map<string, EmployeeUser>();

    employees.forEach((employee) => {
      employeeByUserId.set(employee.userId.trim(), employee);
      employeeByName.set(normalizeText(employee.name), employee);
    });

    const hoursMap: Record<string, number> = {};
    const notMatched: string[] = [];
    const externalMap = new Map<string, ExternalPayrollItem>();
    const detailsByWageKey: Record<string, PayrollDayDetail[]> = {};
    const monthStart = importMonthValue.startOf('month');
    const monthEnd = importMonthValue.endOf('month');

    rows.forEach((row) => {
      const parsedDate = dayjs(row.ngay, ['DD/MM/YYYY', 'D/M/YYYY'], true);
      if (!parsedDate.isValid() || parsedDate.isBefore(monthStart) || parsedDate.isAfter(monthEnd)) {
        return;
      }

      const employeeFromUserId = row.maNV ? employeeByUserId.get(row.maNV.trim()) : undefined;
      const employeeFromName = !employeeFromUserId && row.tenNhanVien ? employeeByName.get(normalizeText(row.tenNhanVien)) : undefined;
      const matchedEmployee = employeeFromUserId ?? employeeFromName;

      if (!matchedEmployee) {
        const externalKey = `${row.maNV || 'N/A'}-${normalizeText(row.tenNhanVien || 'unknown')}`;
        const externalWageKey = makeExternalWageKey(row.maNV || 'N/A', row.tenNhanVien || 'Nhân viên ngoài hệ thống');
        const existingExternal = externalMap.get(externalKey);
        if (existingExternal) {
          existingExternal.totalHours = roundTo2(existingExternal.totalHours + row.totalHours);
        } else {
          externalMap.set(externalKey, {
            maNV: row.maNV || 'N/A',
            tenNhanVien: row.tenNhanVien || 'Nhân viên ngoài hệ thống',
            totalHours: roundTo2(row.totalHours),
          });
        }

        const detailRows = detailsByWageKey[externalWageKey] ?? [];
        detailRows.push({
          ngay: row.ngay,
          maNV: row.maNV || 'N/A',
          tenNhanVien: row.tenNhanVien || 'Nhân viên ngoài hệ thống',
          totalHours: roundTo2(row.totalHours),
        });
        detailsByWageKey[externalWageKey] = detailRows;

        notMatched.push(row.maNV || row.tenNhanVien || 'Không xác định');
        return;
      }

      const current = hoursMap[matchedEmployee.userId] ?? 0;
      hoursMap[matchedEmployee.userId] = roundTo2(current + row.totalHours);

      const wageKey = `sys:${matchedEmployee.userId}`;
      const detailRows = detailsByWageKey[wageKey] ?? [];
      detailRows.push({
        ngay: row.ngay,
        maNV: row.maNV || matchedEmployee.userId,
        tenNhanVien: row.tenNhanVien || matchedEmployee.name,
        totalHours: roundTo2(row.totalHours),
      });
      detailsByWageKey[wageKey] = detailRows;
    });

    Object.keys(detailsByWageKey).forEach((key) => {
      detailsByWageKey[key] = detailsByWageKey[key].sort((a, b) => {
        const dayA = dayjs(a.ngay, ['DD/MM/YYYY', 'D/M/YYYY'], true);
        const dayB = dayjs(b.ngay, ['DD/MM/YYYY', 'D/M/YYYY'], true);
        if (!dayA.isValid() || !dayB.isValid()) {
          return a.ngay.localeCompare(b.ngay);
        }
        return dayA.valueOf() - dayB.valueOf();
      });
    });

    return {
      hoursMap,
      notMatched,
      externalItems: Array.from(externalMap.values()),
      detailsByWageKey,
    };
  };

  const loadPayrollSummary = async () => {
    setLoadingPayroll(true);
    try {
      const rowsFromSystem: PayrollSummaryRow[] = employees
        .map((employee) => {
          const hourlyWage = employee.hourlyWage ?? 0;
          const wageKey = `sys:${employee.userId}`;
          const appliedHourlyWage = wageOverrides[wageKey] ?? hourlyWage;
          const totalHours = importedHoursMap[employee.userId] ?? 0;
          const totalPay = roundTo2(totalHours * appliedHourlyWage);

          return {
            rowId: `sys-${employee.userId}`,
            wageKey,
            userId: employee.userId,
            name: employee.name,
            hourlyWage: appliedHourlyWage,
            totalHours,
            totalPay,
          };
        })
        .filter((item) => item.totalHours > 0);

      const externalRows: PayrollSummaryRow[] = externalPayrollItems.map((item, index) => {
        const wageKey = makeExternalWageKey(item.maNV, item.tenNhanVien);
        const appliedHourlyWage = wageOverrides[wageKey] ?? 0;

        return {
          rowId: `ext-${item.maNV}-${index}`,
          wageKey,
          userId: item.maNV,
          name: item.tenNhanVien,
          hourlyWage: appliedHourlyWage,
          totalHours: roundTo2(item.totalHours),
          totalPay: roundTo2(item.totalHours * appliedHourlyWage),
          isExternal: true,
        };
      });

      const rows = [...rowsFromSystem, ...externalRows].sort((a, b) => {
        if (a.totalPay !== b.totalPay) {
          return b.totalPay - a.totalPay;
        }
        return b.totalHours - a.totalHours;
      });

      setPayrollRows(rows);
    } catch (error) {
      message.error('Không thể tải tổng hợp công và lương');
      setPayrollRows([]);
    } finally {
      setLoadingPayroll(false);
    }
  };

  useEffect(() => {
    if (employees.length === 0) {
      setPayrollRows([]);
      return;
    }
    loadPayrollSummary();
  }, [employees, importedHoursMap, externalPayrollItems, wageOverrides]);

  const handlePickImportFile = (file: RcFile): boolean => {
    setSelectedImportFile(file);
    return false;
  };

  const handleConfirmImport = async () => {
    if (!selectedImportFile) {
      message.error('Vui lòng chọn file Excel trước khi áp dụng');
      return;
    }

    setParsingExcel(true);
    try {
      const fileData = await selectedImportFile.arrayBuffer();
      const workbook = XLSX.read(fileData, {
        type: 'array',
        cellDates: true,
      });

      const worksheet = findTargetWorksheet(workbook);
      const parsedRows = extractRowsFromWorksheet(worksheet);
      const normalizedRows = parseTimesheetRows(parsedRows as unknown as Record<string, unknown>[]);
      const { rows: deduplicatedRows, duplicateCount } = deduplicateTimesheetRows(normalizedRows);
      const { hoursMap, notMatched, externalItems, detailsByWageKey } = mapImportedRowsToEmployees(deduplicatedRows);
      const monthKey = importMonthValue.format('YYYY-MM');
      const monthLabel = importMonthValue.format('MM/YYYY');
      const normalizedMonthRows = deduplicatedRows.map((row) => ({
        ngay: row.ngay,
        maNV: row.maNV,
        tenNhanVien: row.tenNhanVien,
        totalHours: roundTo2(row.totalHours),
      }));

      await payrollTimesheetService.saveMonthlyImport({
        monthKey,
        monthLabel,
        fileName: selectedImportFile.name,
        hoursByUserId: hoursMap,
        externalItems,
        unmatchedRows: notMatched,
        wageOverrides: {},
        rowDetailsByWageKey: detailsByWageKey,
      });

      await payrollTimesheetService.saveMonthlyRows(
        monthKey,
        selectedImportFile.name,
        normalizedMonthRows
      );

      const monthImportData: PayrollTimesheetImport = {
        monthKey,
        monthLabel,
        fileName: selectedImportFile.name,
        hoursByUserId: hoursMap,
        externalItems,
        unmatchedRows: notMatched,
        wageOverrides: {},
        rowDetailsByWageKey: detailsByWageKey,
        uploadedAt: new Date(),
        updatedAt: new Date(),
      };

      monthlyImportCacheRef.current.set(monthKey, {
        monthlyData: monthImportData,
        monthRows: normalizedMonthRows,
      });

      setMonthlyImports((prev) => {
        const filtered = prev.filter((item) => item.monthKey !== monthKey);
        return [monthImportData, ...filtered].sort((a, b) => b.monthKey.localeCompare(a.monthKey));
      });

      applyMonthlyImportToState(monthImportData, normalizedMonthRows);
      setImportModalOpen(false);

      const matchedCount = Object.keys(hoursMap).length;
      const externalCount = externalItems.length;
      if (matchedCount === 0) {
        message.warning('Không tìm thấy nhân viên khớp dữ liệu trong file Excel của tháng đã chọn');
      } else {
        message.success(`Đã nạp bảng công tháng ${importMonthValue.format('MM/YYYY')}: ${matchedCount} nhân viên hệ thống + ${externalCount} nhân viên ngoài hệ thống`);
      }

      if (notMatched.length > 0) {
        message.warning(`Có ${notMatched.length} dòng chưa khớp nhân viên trong hệ thống`);
      }

      if (duplicateCount > 0) {
        message.warning(`Đã phát hiện ${duplicateCount} dòng trùng ngày + nhân viên và tự động loại trùng`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Không đọc được file Excel. Hãy kiểm tra lại định dạng hoặc cấu trúc file.';
      message.error(errorMessage);
      setImportedHoursMap({});
      setExternalPayrollItems([]);
      setWageOverrides({});
      setRowDetailsByWageKey({});
      setMonthlyTimesheetRows([]);
      setUploadedFileName('');
      setUnmatchedRows([]);
    } finally {
      setParsingExcel(false);
    }
  };

  const handleOpenImportModal = () => {
    setImportMonthValue(salaryPeriodValue);
    setSelectedImportFile(null);
    setImportModalOpen(true);
  };

  const handleClearTimesheet = () => {
    setImportedHoursMap({});
    setExternalPayrollItems([]);
    setWageOverrides({});
    setRowDetailsByWageKey({});
    setMonthlyTimesheetRows([]);
    setUploadedFileName('');
    setUnmatchedRows([]);
    setSelectedImportMonthKey('');
    message.success('Đã xóa dữ liệu bảng công đã nạp');
  };

  const handleSelectUploadedMonth = async (monthKey: string) => {
    setSelectedImportMonthKey(monthKey);
    if (!monthKey) {
      setImportedHoursMap({});
      setExternalPayrollItems([]);
      setWageOverrides({});
      setRowDetailsByWageKey({});
      setMonthlyTimesheetRows([]);
      setUploadedFileName('');
      setUnmatchedRows([]);
      return;
    }

    const cached = monthlyImportCacheRef.current.get(monthKey);
    if (cached) {
      applyMonthlyImportToState(cached.monthlyData, cached.monthRows);
      return;
    }

    setLoadingPayroll(true);
    try {
      const monthlyData = await payrollTimesheetService.getMonthlyImport(monthKey);
      if (!monthlyData) {
        message.error('Không tìm thấy dữ liệu tháng đã chọn');
        return;
      }

      const monthRows = await payrollTimesheetService.getMonthlyRows(monthKey);
      monthlyImportCacheRef.current.set(monthKey, {
        monthlyData,
        monthRows,
      });
      applyMonthlyImportToState(monthlyData, monthRows);
    } catch (error) {
      message.error('Không thể tải dữ liệu tháng đã upload');
    } finally {
      setLoadingPayroll(false);
    }
  };

  const handleChangeRowWage = (row: PayrollSummaryRow, value: number | null) => {
    const nextWage = Math.max(0, Number(value ?? 0));
    setWageOverrides((prev) => ({
      ...prev,
      [row.wageKey]: nextWage,
    }));
  };

  const openWageEditor = (row: PayrollSummaryRow) => {
    setWageEditorModal({
      open: true,
      row,
      value: row.hourlyWage,
    });
  };

  const saveWageEditor = () => {
    if (!wageEditorModal.row) {
      return;
    }

    handleChangeRowWage(wageEditorModal.row, wageEditorModal.value);
    setWageEditorModal({ open: false, row: null, value: 0 });
  };

  const openPayrollDetail = (row: PayrollSummaryRow) => {
    setPayrollDetailModal({ open: true, row });
  };

  const getDetailRowsForRow = (row: PayrollSummaryRow | null): PayrollDayDetail[] => {
    if (!row) {
      return [];
    }

    const mappedRows = rowDetailsByWageKey[row.wageKey] ?? [];
    if (mappedRows.length > 0) {
      return mappedRows;
    }

    if (monthlyTimesheetRows.length === 0) {
      return [];
    }

    if (row.isExternal) {
      const normalizedName = normalizeText(row.name);
      return monthlyTimesheetRows.filter((item) => {
        return (
          (row.userId && item.maNV === row.userId) ||
          normalizeText(item.tenNhanVien) === normalizedName
        );
      });
    }

    const normalizedName = normalizeText(row.name);
    return monthlyTimesheetRows.filter((item) => {
      return item.maNV === row.userId || normalizeText(item.tenNhanVien) === normalizedName;
    });
  };

  const payrollDetailRows = useMemo(() => {
    return getDetailRowsForRow(payrollDetailModal.row).sort((a, b) => {
      const dayA = dayjs(a.ngay, ['DD/MM/YYYY', 'D/M/YYYY'], true);
      const dayB = dayjs(b.ngay, ['DD/MM/YYYY', 'D/M/YYYY'], true);
      if (!dayA.isValid() || !dayB.isValid()) {
        return a.ngay.localeCompare(b.ngay);
      }
      return dayA.valueOf() - dayB.valueOf();
    });
  }, [payrollDetailModal.row, rowDetailsByWageKey, monthlyTimesheetRows]);

  const handleSaveWageOverrides = async () => {
    if (!selectedImportMonthKey) {
      message.error('Vui lòng chọn tháng đã upload để lưu mức lương');
      return;
    }

    setSavingWageOverrides(true);
    try {
      await payrollTimesheetService.updateMonthlyWageOverrides(selectedImportMonthKey, wageOverrides);

      setMonthlyImports((prev) =>
        prev.map((item) =>
          item.monthKey === selectedImportMonthKey
            ? {
                ...item,
                wageOverrides,
                updatedAt: new Date(),
              }
            : item
        )
      );

      const cached = monthlyImportCacheRef.current.get(selectedImportMonthKey);
      if (cached) {
        monthlyImportCacheRef.current.set(selectedImportMonthKey, {
          ...cached,
          monthlyData: {
            ...cached.monthlyData,
            wageOverrides,
            updatedAt: new Date(),
          },
        });
      }

      message.success('Đã lưu mức lương áp dụng của tháng');
    } catch (error) {
      message.error('Không thể lưu mức lương áp dụng');
    } finally {
      setSavingWageOverrides(false);
    }
  };

  const openCreateEmployeeModal = () => {
    setEditingEmployee(null);
    employeeForm.resetFields();
    employeeForm.setFieldsValue({
      followedOA: true,
      isSensitive: false,
      isAdmin: false,
      dataEntryRole: false,
      employmentType: 'parttime',
      defaultPosition: 'table',
      hourlyWage: 0,
    });
    setEmployeeModalOpen(true);
  };

  const openEditEmployeeModal = (employee: EmployeeUser) => {
    setEditingEmployee(employee);
    employeeForm.setFieldsValue({
      ...employee,
      employmentType: employee.employmentType ?? 'parttime',
      defaultPosition: employee.defaultPosition ?? 'table',
      followedOA: Boolean(employee.followedOA),
      isSensitive: Boolean(employee.isSensitive),
      isAdmin: Boolean(employee.isAdmin),
      dataEntryRole: Boolean(employee.dataEntryRole),
      hourlyWage: employee.hourlyWage ?? 0,
    });
    setEmployeeModalOpen(true);
  };

  const handleSaveEmployee = async (values: {
    userId: string;
    name: string;
    avatar: string;
    idByOA: string;
    followedOA: boolean;
    isSensitive: boolean;
    isAdmin: boolean;
    dataEntryRole: boolean;
    employmentType: EmploymentType;
    defaultPosition: DefaultPosition;
    hourlyWage: number;
  }) => {
    setSavingEmployee(true);
    try {
      if (editingEmployee) {
        await employeeUserService.updateUserByUserId(editingEmployee.userId, values, 'admin');
        message.success('Cập nhật nhân viên thành công');
      } else {
        const existedUser = await employeeUserService.getUserByUserId(values.userId);
        if (existedUser) {
          await employeeUserService.updateUserByUserId(values.userId, values, 'admin');
          message.success('Nhân viên đã tồn tại, đã cập nhật thông tin');
        } else {
          await employeeUserService.addUser(values);
          message.success('Thêm nhân viên thành công');
        }
      }

      setEmployeeModalOpen(false);
      employeeForm.resetFields();
      await loadEmployees();
    } catch (error) {
      message.error('Không thể lưu thông tin nhân viên');
    } finally {
      setSavingEmployee(false);
    }
  };

  const handleDeleteEmployee = (employee: EmployeeUser) => {
    Modal.confirm({
      title: 'Xác nhận xóa nhân viên',
      content: `Bạn có chắc chắn muốn xóa nhân viên ${employee.name} và dữ liệu lịch liên quan không?`,
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      async onOk() {
        try {
          if (employee.isAdmin) {
            message.error('Không thể xóa tài khoản admin');
            return;
          }
          await workScheduleService.deleteSchedulesByUserId(employee.userId);
          await employeeUserService.deleteUserByUserId(employee.userId);
          message.success('Đã xóa nhân viên và dữ liệu lịch liên quan');
          await loadEmployees();
        } catch (error) {
          message.error('Không thể xóa nhân viên');
        }
      },
    });
  };

  const employeeColumns: ColumnsType<EmployeeUser> = [
    {
      title: 'Nhân viên',
      key: 'name',
      render: (_, record) => (
        <Space>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {record.avatar ? (
              <img src={record.avatar} alt={record.name} style={{ width: 32, height: 32, objectFit: 'cover' }} />
            ) : (
              <TeamOutlined />
            )}
          </div>
          <div>
            <div>{record.name}</div>
            <Text type="secondary">{record.userId}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      width: 130,
      render: (_, record) => getRoleTag(record),
    },
    {
      title: 'Loại nhân viên',
      dataIndex: 'employmentType',
      width: 150,
      render: (value: EmploymentType | undefined) => getEmploymentText(value),
    },
    {
      title: 'Vị trí mặc định',
      dataIndex: 'defaultPosition',
      width: 150,
      render: (value: DefaultPosition | undefined) => getPositionText(value),
    },
    {
      title: 'Lương/giờ',
      dataIndex: 'hourlyWage',
      width: 140,
      render: (value: number | undefined) => formatCurrency(value ?? 0),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 130,
      fixed: isMobile ? undefined : 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <Button type="link" icon={<EditOutlined />} onClick={() => openEditEmployeeModal(record)} />
          </Tooltip>
          <Tooltip title="Xóa">
            <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteEmployee(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const payrollColumns: ColumnsType<PayrollSummaryRow> = [
    {
      title: 'Nhân viên',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (value: string, record) => (
        <div>
          <Space size={6}>
            <span>{value}</span>
            {record.isExternal && <Tag color="orange">Ngoài hệ thống</Tag>}
          </Space>
          <div>
            <Text type="secondary">{record.userId}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Lương/giờ',
      dataIndex: 'hourlyWage',
      key: 'hourlyWage',
      width: 150,
      render: (value: number) => formatCurrency(value),
    },
    {
      title: 'Tổng giờ',
      dataIndex: 'totalHours',
      key: 'totalHours',
      width: 120,
      render: (value: number) => `${roundTo2(value)} giờ`,
    },
    {
      title: 'Tạm tính lương',
      dataIndex: 'totalPay',
      key: 'totalPay',
      width: 160,
      render: (value: number) => <Text strong>{formatCurrency(value)}</Text>,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 220,
      render: (_: unknown, record) => (
        <Space>
          <Button type="link" onClick={() => openWageEditor(record)}>
            Sửa lương
          </Button>
          <Button type="link" onClick={() => openPayrollDetail(record)}>
            Chi tiết
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1>Quản lý nhân sự & phân quyền</h1>
          <Tag color="blue">{employees.length} nhân viên</Tag>
        </div>
      </div>

      <div className="admin-page-header">
        <div className="admin-search-filters" style={{ flex: 1 }}>
          <Search placeholder="Tìm theo tên, userId hoặc id OA" allowClear onChange={(event) => setSearchText(event.target.value)} />
        </div>
        <div className="admin-page-actions">
          <Button icon={<ReloadOutlined />} onClick={loadEmployees}>
            Tải lại
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEmployeeModal}>
            Thêm nhân viên
          </Button>
        </div>
      </div>

      <div className="admin-table-container">
        <Table
          className="admin-table"
          rowKey="userId"
          loading={loadingEmployees}
          columns={employeeColumns}
          dataSource={filteredEmployees}
          scroll={{ x: 1060 }}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
        />
      </div>

      <div className="admin-form-section" style={{ padding: 16, marginTop: 16 }}>
        <div className="admin-page-header" style={{ marginBottom: 12 }}>
          <div className="admin-form-section-title" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
            Tổng hợp công & lương theo file Excel tháng ({periodLabel})
          </div>
          <div className="admin-page-actions" style={{ gap: 8 }}>
            <Select
              style={{ minWidth: 180 }}
              placeholder="Chọn tháng đã upload"
              value={selectedImportMonthKey || undefined}
              loading={loadingMonthlyImports}
              allowClear
              onChange={(value) => handleSelectUploadedMonth(value ?? '')}
              options={monthlyImports.map((item) => ({
                label: `${item.monthLabel} - ${item.fileName}`,
                value: item.monthKey,
              }))}
            />
            <Button icon={<UploadOutlined />} onClick={handleOpenImportModal}>
              Nhập bảng công tháng
            </Button>
            <Button onClick={handleClearTimesheet} disabled={!uploadedFileName}>
              Xóa file
            </Button>
            <Button type="primary" onClick={handleSaveWageOverrides} loading={savingWageOverrides} disabled={!selectedImportMonthKey}>
              Lưu lương tháng
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadPayrollSummary} loading={loadingPayroll}>
              Tải lại
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          {uploadedFileName ? (
            <Space wrap>
              <Tag color="geekblue">Tháng: {salaryPeriodValue.format('MM/YYYY')}</Tag>
              <Tag color="blue">File: {uploadedFileName}</Tag>
              <Tag color="green">Đã khớp: {Object.keys(importedHoursMap).length} nhân viên</Tag>
              {externalPayrollItems.length > 0 && <Tag color="orange">Ngoài hệ thống: {externalPayrollItems.length} nhân viên</Tag>}
            </Space>
          ) : (
            <Text type="secondary">Vui lòng chọn tháng đã upload hoặc nhập file Excel mới để tính lương.</Text>
          )}
          {unmatchedRows.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text type="warning">Dòng chưa khớp: {unmatchedRows.slice(0, 8).join(', ')}{unmatchedRows.length > 8 ? '...' : ''}</Text>
            </div>
          )}
        </div>

        <Table
          className="admin-table"
          rowKey="rowId"
          loading={loadingPayroll}
          columns={payrollColumns}
          dataSource={payrollRows}
          scroll={{ x: 760 }}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
        />
      </div>

      <Modal
        title="Nhập bảng công từ Excel"
        open={importModalOpen}
        onCancel={() => {
          if (!parsingExcel) {
            setImportModalOpen(false);
          }
        }}
        onOk={handleConfirmImport}
        okText="Áp dụng"
        cancelText="Hủy"
        confirmLoading={parsingExcel}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <Text strong>Chọn tháng tính lương</Text>
            <DatePicker
              picker="month"
              value={importMonthValue}
              allowClear={false}
              onChange={(value) => {
                if (value) {
                  setImportMonthValue(value);
                }
              }}
              style={{ width: '100%', marginTop: 8 }}
            />
          </div>

          <div>
            <Text strong>Chọn file Excel bảng công</Text>
            <Upload
              beforeUpload={handlePickImportFile}
              showUploadList={false}
              accept=".xlsx,.xls"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>
                Chọn file
              </Button>
            </Upload>
            <div style={{ marginTop: 8 }}>
              {selectedImportFile ? (
                <Text type="secondary">File đã chọn: {selectedImportFile.name}</Text>
              ) : (
                <Text type="secondary">Chưa chọn file</Text>
              )}
            </div>
          </div>

          <Text type="secondary">
            File cần đúng cấu trúc chấm công, ưu tiên sheet "{PREFERRED_SHEET}" và có dòng tiêu đề chứa "{HEADER_KEY}".
          </Text>
        </Space>
      </Modal>

      <Modal
        title={wageEditorModal.row ? `Sửa lương/giờ - ${wageEditorModal.row.name}` : 'Sửa lương/giờ'}
        open={wageEditorModal.open}
        onCancel={() => setWageEditorModal({ open: false, row: null, value: 0 })}
        onOk={saveWageEditor}
        okText="Áp dụng"
        cancelText="Hủy"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <InputNumber
            min={0}
            value={wageEditorModal.value}
            onChange={(value) => setWageEditorModal((prev) => ({ ...prev, value: Number(value ?? 0) }))}
            style={{ width: '100%' }}
            addonAfter="đ/giờ"
          />
          <Text type="secondary">Sau khi áp dụng, bấm "Lưu lương tháng" để lưu cố định cho tháng này.</Text>
        </Space>
      </Modal>

      <Modal
        title={payrollDetailModal.row ? `Chi tiết ngày công - ${payrollDetailModal.row.name}` : 'Chi tiết ngày công'}
        open={payrollDetailModal.open}
        onCancel={() => setPayrollDetailModal({ open: false, row: null })}
        footer={null}
        width={780}
      >
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">Tổng giờ chi tiết: {roundTo2(payrollDetailRows.reduce((sum, item) => sum + item.totalHours, 0))} giờ</Text>
        </div>
        <Table
          rowKey={(record) => `${record.ngay}-${record.maNV}-${record.totalHours}`}
          dataSource={payrollDetailRows}
          pagination={{ pageSize: 8, showSizeChanger: false }}
          columns={[
            { title: 'Ngày', dataIndex: 'ngay', width: 120 },
            { title: 'Mã NV', dataIndex: 'maNV', width: 140 },
            { title: 'Tên nhân viên', dataIndex: 'tenNhanVien' },
            {
              title: 'Số giờ',
              dataIndex: 'totalHours',
              width: 120,
              render: (value: number) => `${roundTo2(value)} giờ`,
            },
          ]}
        />
      </Modal>

      <Modal
        title={editingEmployee ? 'Chỉnh sửa nhân viên' : 'Thêm nhân viên'}
        open={employeeModalOpen}
        onCancel={() => {
          setEmployeeModalOpen(false);
          setEditingEmployee(null);
          employeeForm.resetFields();
        }}
        footer={null}
        width={800}
        className="admin-modal"
      >
        <Form form={employeeForm} layout="vertical" onFinish={handleSaveEmployee}>
          <div className="admin-form-section" style={{ padding: 16 }}>
            <div className="admin-form-section-title">Thông tin nhân viên</div>
            <div className="admin-form-row">
              <Form.Item name="userId" label="User ID" rules={[{ required: true, message: 'Vui lòng nhập userId' }]}>
                <Input placeholder="Nhập userId ổn định" disabled={Boolean(editingEmployee)} />
              </Form.Item>
              <Form.Item name="name" label="Tên nhân viên" rules={[{ required: true, message: 'Vui lòng nhập tên nhân viên' }]}>
                <Input placeholder="Nhập tên nhân viên" />
              </Form.Item>
              <Form.Item name="idByOA" label="ID theo OA" rules={[{ required: true, message: 'Vui lòng nhập idByOA' }]}>
                <Input placeholder="Nhập idByOA" />
              </Form.Item>
              <Form.Item name="avatar" label="Ảnh đại diện URL">
                <Input placeholder="https://..." />
              </Form.Item>
              <Form.Item name="employmentType" label="Loại nhân viên" rules={[{ required: true, message: 'Vui lòng chọn loại nhân viên' }]}>
                <Select
                  options={[
                    { label: 'Toàn thời gian', value: 'fulltime' },
                    { label: 'Bán thời gian', value: 'parttime' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="defaultPosition" label="Vị trí mặc định" rules={[{ required: true, message: 'Vui lòng chọn vị trí mặc định' }]}>
                <Select
                  options={[
                    { label: 'Table', value: 'table' },
                    { label: 'Bar', value: 'bar' },
                    { label: 'Security', value: 'security' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="hourlyWage" label="Mức lương theo giờ" rules={[{ required: true, message: 'Vui lòng nhập mức lương theo giờ' }]}>
                <InputNumber min={0} style={{ width: '100%' }} addonAfter="đ/giờ" />
              </Form.Item>
              <Form.Item name="followedOA" label="Đã follow OA" valuePropName="checked">
                <Switch checkedChildren="Có" unCheckedChildren="Không" />
              </Form.Item>
              <Form.Item name="isSensitive" label="Dữ liệu nhạy cảm" valuePropName="checked">
                <Switch checkedChildren="Có" unCheckedChildren="Không" />
              </Form.Item>
              <Form.Item name="isAdmin" label="Quyền admin" valuePropName="checked">
                <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
              </Form.Item>
              <Form.Item name="dataEntryRole" label="Quyền nhập liệu" valuePropName="checked">
                <Switch checkedChildren="Bật" unCheckedChildren="Tắt" />
              </Form.Item>
            </div>
          </div>

          <Space>
            <Button type="primary" htmlType="submit" loading={savingEmployee}>
              {editingEmployee ? 'Lưu thay đổi' : 'Thêm nhân viên'}
            </Button>
            <Button
              onClick={() => {
                setEmployeeModalOpen(false);
                setEditingEmployee(null);
                employeeForm.resetFields();
              }}
            >
              Hủy
            </Button>
          </Space>
        </Form>
      </Modal>
    </AdminLayout>
  );
};

export default EmployeeRoleManagement;
